"use client";

import Link from "next/link";
import { BlockRenderer } from "@/components/block-renderer";
import { Img } from "@/components/image";
import { buttonVariants } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { HelpGuideType } from "@/features/help-center/types/help-center.type";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

type HelpGuideProps = {
  data: HelpGuideType;
};

export const HelpGuide = ({ data }: HelpGuideProps) => {
  const steps = data.steps.filter((step) => step.imageDesktop?.url);
  const [firstStep] = steps;

  if (!firstStep) {
    return null;
  }

  return (
    <section>
      <header className="bg-background-fill-primary-pressed p-6">
        <h2 className="font-medium text-brand-green1 text-xl">{data.title}</h2>
        {data.description && (
          <p className="mt-2 text-base text-brand-green1">{data.description}</p>
        )}
      </header>
      <Tabs className="w-full" defaultValue={firstStep.id.toString()}>
        <TabsList className="no-scrollbar flex h-auto w-full justify-start gap-2 overflow-x-auto bg-transparent px-4 pt-6 pb-0 md:px-6">
          {steps.map((step) => (
            <TabsTrigger
              className="shrink-0 rounded-full border border-border bg-background-fill-primary px-5 py-2 text-base text-text data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
              key={step.id}
              value={step.id.toString()}
            >
              {step.tabLabel}
            </TabsTrigger>
          ))}
        </TabsList>
        {steps.map((step) => {
          const desktopImage = step.imageDesktop;
          const mobileImage = step.imageMobile ?? desktopImage;

          return (
            <TabsContent
              className="mt-0 px-4 pt-7 pb-4 md:px-0 md:py-20"
              key={step.id}
              value={step.id.toString()}
            >
              <div className="mx-auto flex flex-col md:grid md:max-w-230.25 md:grid-cols-[minmax(0,1fr)_392px] md:gap-x-14 md:gap-y-9 md:px-14">
                {desktopImage && mobileImage && (
                  <div className="order-2 mt-7 md:order-none md:col-start-1 md:row-span-2 md:mt-0 md:self-center">
                    <Img
                      alt={desktopImage.alternativeText ?? ""}
                      className="mx-auto hidden h-auto w-full max-w-full rounded-lg object-contain md:block md:max-h-55 md:w-auto"
                      height={desktopImage.height}
                      src={strapiUrl(desktopImage.url)}
                      width={desktopImage.width}
                    />
                    <Img
                      alt={mobileImage.alternativeText ?? ""}
                      className="mx-auto h-auto max-h-113 w-auto max-w-full rounded-lg object-contain md:hidden"
                      height={mobileImage.height}
                      src={strapiUrl(mobileImage.url)}
                      width={mobileImage.width}
                    />
                  </div>
                )}
                <div className="order-1 px-3 md:order-none md:col-start-2 md:row-start-1 md:self-end md:px-0">
                  <h3 className="hidden font-medium md:block">{step.title}</h3>
                  <div className="text-brand-green1 md:mt-6">
                    <BlockRenderer content={step.description} />
                  </div>
                </div>
                {step.cta && (
                  <Link
                    className={cn(
                      buttonVariants({ variant: "secondary", size: "wide" }),
                      "order-3 mt-8 w-full md:order-none md:col-start-2 md:row-start-2 md:mt-0 md:w-68.5 md:self-start",
                    )}
                    href={step.cta.url}
                  >
                    {step.cta.title}
                  </Link>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
      <div className="mx-4 border-border border-b md:mx-6" />
    </section>
  );
};
